import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../lib/google_maps_setup"

export default class extends Controller {
  static targets = ["map", "latitude", "longitude"]
  static values = {
    lat: { type: Number, default: 14.5547 },
    lng: { type: Number, default: 121.0244 },
    zoom: { type: Number, default: 13 },
  }

  async connect() {
    try {
      await this.initMap()
    } catch (error) {
      console.error("Location picker:", error.message)
    }
  }

  disconnect() {
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map)
    }
    this.map = null
    this.marker = null
  }

  async initMap() {
    const lat = parseFloat(this.latitudeTarget.value) || this.latValue
    const lng = parseFloat(this.longitudeTarget.value) || this.lngValue
    const center = { lat, lng }

    const { Map, AdvancedMarkerElement } = await loadGoogleMaps()
    this.AdvancedMarkerElement = AdvancedMarkerElement

    this.map = new Map(this.mapTarget, {
      center,
      zoom: this.zoomValue,
      mapId: "location-picker",
      mapTypeControl: true,
      mapTypeControlOptions: {
        position: google.maps.ControlPosition.BOTTOM_RIGHT,
      },
      streetViewControl: false,
      fullscreenControl: false,
    })

    // Place marker if lat/lng already set
    if (this.latitudeTarget.value && this.longitudeTarget.value) {
      this.createMarker(center)
    }

    // Click to place/move marker
    this.map.addListener("click", (e) => {
      this.placeMarker(e.latLng.lat(), e.latLng.lng())
    })

    // Add autocomplete search
    await this.initAutocomplete()
  }

  async initAutocomplete() {
    await google.maps.importLibrary("places")

    const autocomplete = new google.maps.places.PlaceAutocompleteElement({
      locationBias: this.map.getCenter(),
    })

    const card = document.createElement("div")
    card.style.cssText = "margin: 10px;"
    card.appendChild(autocomplete)

    this.map.controls[google.maps.ControlPosition.TOP_LEFT].push(card)

    // Handle place selection
    autocomplete.addEventListener("gmp-select", async ({ placePrediction }) => {
      try {
        const place = placePrediction.toPlace()
        await place.fetchFields({ fields: ["location", "viewport"] })

        if (place.viewport) {
          this.map.fitBounds(place.viewport)
        } else {
          this.map.setCenter(place.location)
          this.map.setZoom(16)
        }

        const lat = place.location.lat()
        const lng = place.location.lng()
        this.placeMarker(lat, lng)
      } catch (error) {
        console.error("Place selection failed:", error.message)
      }
    })
  }

  createMarker(position) {
    this.marker = new this.AdvancedMarkerElement({
      map: this.map,
      position,
      gmpDraggable: true,
    })
    this.marker.addListener("dragend", () => {
      const pos = this.marker.position
      this.updateLatLng(pos.lat, pos.lng)
    })
  }

  placeMarker(lat, lng) {
    const position = { lat, lng }

    if (this.marker) {
      this.marker.position = position
    } else {
      this.createMarker(position)
    }

    this.updateLatLng(lat, lng)
  }

  updateLatLng(lat, lng) {
    this.latitudeTarget.value = lat.toFixed(7)
    this.longitudeTarget.value = lng.toFixed(7)
  }
}
